import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogPresets } from '@principle-theorem/ng-shared';
import { ClinicalNote, Patient } from '@principle-theorem/principle-core';
import {
  IClinicalNote,
  IPatient,
  IReferralClinicalNote,
  IReferralClinicalNotesChanged,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  isSameIdentifier,
  multiSort,
  snapshot,
  sortMoment,
  toMoment,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  ClinicalNoteSelectionDialogComponent,
  IClinicalNoteSelectionDialogData,
} from '../clinical-note-selection-dialog/clinical-note-selection-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'pr-referral-add-clinical-notes',
  imports: [MatButtonModule, MatIconModule, MatMenuModule],
  templateUrl: './referral-add-clinical-notes.component.html',
  styleUrl: './referral-add-clinical-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralAddClinicalNotesComponent {
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  patient = input.required<WithRef<IPatient>>();
  clinicalNotes = input<WithRef<IReferralClinicalNote>[]>([]);
  label = input<string>('Add Clinical Note');
  clinicalNotesChanged = output<IReferralClinicalNotesChanged>();

  async selectClinicalNotes(): Promise<void> {
    const clinicalNotes = await snapshot(this._getPatientClinicalNotes$());

    if (!clinicalNotes.length) {
      this._snackBar.open('No clinical notes available to select');
      return;
    }

    const currentlySelected = this.clinicalNotes().map(
      (refNote) => refNote.clinicalNote
    );

    const selected = await this._dialog
      .open<
        ClinicalNoteSelectionDialogComponent,
        IClinicalNoteSelectionDialogData,
        WithRef<IClinicalNote>[]
      >(
        ClinicalNoteSelectionDialogComponent,
        DialogPresets.large({
          data: {
            clinicalNotes,
            selectedClinicalNotes: currentlySelected,
          },
        })
      )
      .afterClosed()
      .toPromise();

    if (selected === undefined) {
      return;
    }

    const currentReferralNotes = this.clinicalNotes();
    const added = selected.filter(
      (selectedNote) =>
        !currentReferralNotes.some((refNote) =>
          isSameIdentifier(refNote.clinicalNote, selectedNote)
        )
    );
    const removed = currentReferralNotes.filter(
      (refNote) =>
        !selected.some((selectedNote) =>
          isSameIdentifier(refNote.clinicalNote, selectedNote)
        )
    );

    this.clinicalNotesChanged.emit({ added, removed });
  }

  private _getPatientClinicalNotes$(): Observable<WithRef<IClinicalNote>[]> {
    const patient = this.patient();
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      ClinicalNote.all$
    ).pipe(
      switchMap((notes) =>
        ClinicalNote.filterEmpty$(notes).pipe(
          multiSort((noteA, noteB) =>
            sortMoment(toMoment(noteA.recordDate), toMoment(noteB.recordDate))
          )
        )
      )
    );
  }
}
